import { Download, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { RepositoryAsset, RepositorySection } from '@/types'
import { RepositoryAssetThumbnail } from '@/components/RepositoryAssetThumbnail'
import { toDisplayDate } from '@/lib/utils'

interface Props {
  asset: RepositoryAsset
  section: RepositorySection
  onDownload: (asset: RepositoryAsset) => void
  onEdit: (asset: RepositoryAsset) => void
  onDemote: (asset: RepositoryAsset) => void
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${Math.round((bytes / Math.pow(1024, i)) * 10) / 10} ${units[i]}`
}

function formatDate(iso?: string) {
  if (!iso) return ''
  return (toDisplayDate(iso) ?? new Date(iso)).toLocaleDateString()
}

export function RepositoryAssetRow({ asset, section, onDownload, onEdit, onDemote }: Props) {
  return (
    <div className="flex items-start gap-3 rounded-md border bg-card px-3 py-2.5 hover:bg-muted transition-colors">
      <RepositoryAssetThumbnail
        contentVersionId={asset.id}
        fileType={asset.file_type}
        hasThumbnail={!!asset.thumbnail_url}
        title={asset.title}
        size="row"
        className="shrink-0"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h3 className="truncate text-sm font-semibold leading-tight">{asset.title}</h3>
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {asset.asset_type || '—'}
          </Badge>
        </div>
        {asset.repository_summary && (
          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {asset.repository_summary}
          </p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          {asset.file_type && (
            <Badge variant="outline" className="text-[10px]">
              {asset.file_type}
            </Badge>
          )}
          <span>{formatFileSize(asset.content_size)}</span>
          <span>·</span>
          <span>Updated {formatDate(asset.last_modified_date)}</span>
          {asset.owner_name && (
            <>
              <span>·</span>
              <span>{asset.owner_name}</span>
            </>
          )}
          {section === 'Product' && asset.products && asset.products.length > 0 && (
            <span className="flex flex-wrap items-center gap-1">
              <span>·</span>
              {asset.products.map((p) => (
                <Badge
                  key={`${asset.id}-${p.product_id}`}
                  variant="outline"
                  className="text-[10px]"
                >
                  {p.product_name || p.product_id}
                </Badge>
              ))}
            </span>
          )}
          {asset.tags && asset.tags.length > 0 && (
            <span className="flex flex-wrap items-center gap-1">
              <span>·</span>
              {asset.tags.map((tag) => (
                <Badge key={`${asset.id}-tag-${tag}`} variant="outline" className="text-[10px]">
                  #{tag}
                </Badge>
              ))}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDownload(asset)}
          aria-label={`Download ${asset.title}`}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(asset)}
          aria-label={`Edit ${asset.title}`}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDemote(asset)}
          aria-label={`Remove ${asset.title} from repository`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}
